import { useState, useCallback, useMemo, useEffect } from "react"
import { Form } from "antd"
import dayjs, { Dayjs } from "dayjs"
import { useSchedule } from "../ScheduleContext"
import { useSchedulingWorkAreasQuery } from "../scheduleQueries"
import { determineEndDate } from "./workdaysCalculator"
import { Duration } from "../../common/durationModel"
import {
  calculateRepeatEndsOnFromOccurrences,
  calculateOccurrencesFromEndDate,
  buildAllDayChangeUpdates,
  buildCrewSelectUpdates,
  buildWorkareaScheduleUpdates,
  areRecurringFieldsValid,
  reconcileFormValues,
} from "./eventFormModel"
import { EventFormData, EventFormState } from "./eventFormTypes"

export type { EventFormData, EventFormState } from "./eventFormTypes"

type Options = {
  jobId: number
  defaultDate?: Dayjs
  defaultDurationMinutes?: number
}

export const useEventFormState = ({
  jobId,
  defaultDate,
  defaultDurationMinutes = 60,
}: Options): EventFormState => {
  const [form] = Form.useForm()

  const [formData, setFormData] = useState<EventFormData>({
    startDate: defaultDate ?? dayjs(),
    startTime: dayjs("08:00", "HH:mm"),
    estimatedDuration: Duration.minutes(defaultDurationMinutes),
    endDate: defaultDate ?? dayjs(),
    isAllDay: true,
    selectedCrew: [],
    crewLeadId: null,
    sendCrewLeadNotifications: false,
    selectedWorkareaIds: [],
    repeatUnit: "none",
    repeatCount: 1,
    repeatPlace: ["1st"],
    repeatQuarter: [1],
    repeatMonth: [dayjs().month() + 1],
    repeatEndType: 'date',
    repeatOccurrences: 1,
    repeatEndsOn: dayjs(),
    endDateMode: "sameDay",
    manhoursPerDay: 8,
    totalEstimatedHours: 0,
    workDays: [1, 2, 3, 4, 5],
    notes: '',
    frozen: false,
  })

  const { crew } = useSchedule()
  const { data: workAreas } = useSchedulingWorkAreasQuery(jobId)

  const allWorkareas = useMemo(() =>
    [...(workAreas || [])].sort((a, b) => a.groupSortIndex - b.groupSortIndex || a.sortIndex - b.sortIndex),
    [workAreas]
  )
  const assignedWorkareas = allWorkareas.filter((w) =>
    formData.selectedWorkareaIds.includes(w.id)
  )

  const calculatedEndDate = useMemo(() => {
    if (formData.endDateMode !== "calculated" || formData.totalEstimatedHours <= 0 || formData.manhoursPerDay <= 0) {
      return null
    }
    return determineEndDate(formData.startDate, formData.totalEstimatedHours, formData.manhoursPerDay, formData.workDays)
  }, [formData.endDateMode, formData.startDate, formData.manhoursPerDay, formData.totalEstimatedHours, formData.workDays])

  useEffect(() => {
    if (calculatedEndDate) {
      setFormData((prev) => ({ ...prev, endDate: calculatedEndDate }))
    }
  }, [calculatedEndDate])

  const setFormValues = useCallback((updates: Partial<EventFormData>) => {
    setFormData((prev) => reconcileFormValues(prev, updates))
  }, [])

  const handleAllDayChange = useCallback((checked: boolean) => {
    setFormData((prev) => ({ ...prev, ...buildAllDayChangeUpdates(checked, prev, defaultDurationMinutes) }))
  }, [defaultDurationMinutes])

  const handleCrewSelect = useCallback((value: number[] | undefined) => {
    if (!value || !Array.isArray(value)) {
      setFormData((prev) => ({ ...prev, selectedCrew: [], crewLeadId: null }))
      return
    }
    setFormData((prev) => ({ ...prev, ...buildCrewSelectUpdates(value, crew || [], prev.crewLeadId) }))
  }, [crew])

  const removeCrewMember = useCallback((id: number) => {
    setFormData((prev) => ({
      ...prev,
      selectedCrew: prev.selectedCrew.filter((member) => member.id !== id),
      crewLeadId: prev.crewLeadId === id ? null : prev.crewLeadId,
    }))
  }, [])

  const toggleCrewLead = useCallback((id: number) => {
    setFormData((prev) => ({
      ...prev,
      crewLeadId: prev.crewLeadId === id ? null : id,
    }))
  }, [])

  const removeWorkarea = useCallback((workareaId: number) => {
    setFormData((prev) => ({
      ...prev,
      selectedWorkareaIds: prev.selectedWorkareaIds.filter((id) => id !== workareaId),
    }))
  }, [])

  const applyWorkareaSchedule = useCallback((configJson: string) => {
    setFormData((prev) => {
      const { formUpdates, formFieldValues } = buildWorkareaScheduleUpdates(configJson, prev, defaultDurationMinutes)
      form.setFieldsValue(formFieldValues)
      const next = { ...prev, ...formUpdates }

      if (next.repeatEndType === 'occurrences') {
        const newEnd = calculateRepeatEndsOnFromOccurrences(next)
        if (newEnd) {
          next.repeatEndsOn = newEnd
        }
      } else if (next.repeatEndType === 'date') {
        const count = calculateOccurrencesFromEndDate(next)
        if (count !== null) {
          next.repeatOccurrences = count
        }
      }

      return next
    })
  }, [form, defaultDurationMinutes])

  const areRecurringFieldsValidCallback = useCallback(
    () => areRecurringFieldsValid({
      repeatUnit: formData.repeatUnit,
      repeatPlace: formData.repeatPlace,
      repeatMonth: formData.repeatMonth,
    }),
    [formData.repeatUnit, formData.repeatPlace, formData.repeatMonth]
  )

  return {
    form,
    formValues: formData,
    setFormValues,
    crew,
    allWorkareas,
    assignedWorkareas,
    handleAllDayChange,
    handleCrewSelect,
    removeCrewMember,
    toggleCrewLead,
    removeWorkarea,
    applyWorkareaSchedule,
    areRecurringFieldsValid: areRecurringFieldsValidCallback,
    calculatedEndDate,
  }
}
